import {differenceInDays} from 'date-fns/differenceInDays'
import {parseISO} from 'date-fns/parseISO'
import {isVioOfferOrBundleSelected} from 'modules/accommodation/utils'
import {getSearchParameters} from 'modules/sapiSearch/selectors/searchParameters'
import {curry, isEmpty, isNil, pick} from 'ramda'
import {createSelector} from 'reselect'
import {RootState} from 'store'
import {SearchParamsType} from 'types/Search'
import {getHotelIdFromAccommodationPage} from 'utils/getHotelIdFromAccommodationPage'
import {getSerializedUrlParams} from 'utils/searchParams'
import {getCheckInCheckOutDates} from 'utils/server/searchParameters/utils/dates'
import {
  getIsFromTripAdvisor,
  getTrafficSource,
  hasDefaultDates
} from 'utils/trafficSource'

// eslint-disable-next-line -- Need direct selector access for use in other selectors, can't use the hook here
import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {
  getIsAuthenticated,
  getIsPrivateDeal
} from '@vio/core/src/auth/modules/selectors'
import {resolveCapabilities} from '@vio/core/src/brand/capabilities/resolveCapabilities'
import {CommerceCapabilityContext} from '@vio/core/src/brand/capabilities/types/domains'
import {
  getGsiteValueFromUrl,
  getVfValueFromUrl
} from '@vio/core/src/offer/business/privateDeals'
import {GenericOffer} from '@vio/core/src/offer/types/offer'
import {isOtaModeProfile, type OfferMode} from '@vio/core/src/profile'
import {
  SORT_FIELD_POPULARITY,
  SORT_ORDER_ASCENDING
} from '@vio/core/src/searchParams/constants'
import {DateValidationReason} from '@vio/core/src/searchParams/dateValidationReason'
import {
  dateStringToMiddayDate,
  dateToMiddayDate
} from '@vio/core/src/utils/date'
import {parseQueryString} from '@vio/core/src/utils/url'

import {
  filterParamNames,
  HOSTEL_PROPERTY_TYPE_ID,
  persistedBaseUrlParamKeys,
  persistedUrlParamKeys,
  persistedUTMParamsKeys,
  SORT_FIELD_DISTANCE,
  SORT_FIELD_PRICE
} from './constants'
import {omitSeoSortField} from './sortField'
import {
  FilterUrlParams,
  Itinerary,
  LocationSearchType,
  SearchUrlParams,
  SortUrlParams
} from './types'

import type {ParameterizedCapabilityValue} from '@vio/core/src/brand/capabilities/types/core'
import type {BookingHintConfig} from '@vio/core/src/brand/capabilities/types/domains'

type StateType = RootState

type FilterValueType = string | string[] | undefined

const PREFERRED_RATE = 'preferred_rate'

export const getError = createSelector(
  [(state: StateType) => state.search.error],
  error => error
)

/**
 * Gets the current query string from the `search` slice of state
 */
export const getQueryString = (state: StateType): string =>
  state.search.queryString

/**
 * Gets the serialized URL params from the query string with temporary params omitted
 */
export const getUrlParams = createSelector(
  [getQueryString],
  (queryString): SearchUrlParams => getSerializedUrlParams(queryString)
)

const getFilterValue = curry(
  (
    urlParams: FilterUrlParams,
    filterName: string
  ): string | string[] | null | undefined => {
    const filterValue = urlParams[filterName]

    // When user click 0+ guest ratings, the value is 0,  but the filter is not actually active
    if (filterName === 'reviewScore' && filterValue === '0') return

    return filterValue
  }
)

// Note: this relies on the searchParams object only getting recreated when a param actually changed
// https://github.com/reduxjs/reselect#q-why-is-my-selector-recomputing-when-the-input-state-stays-the-same
export const getFiltersFromUrlParams: (
  arg0: StateType
) => Record<string, FilterValueType> = createSelector(
  [getUrlParams],
  urlParams => {
    return filterParamNames.reduce((previous, current) => {
      const filterValue = getFilterValue(urlParams)(current)

      if (filterValue !== undefined) {
        return {
          ...previous,
          [current]: filterValue
        }
      }

      return previous
    }, {})
  }
)

/**
 * Get the keys of the currently active filters
 */
export const getActiveFiltersKeys = createSelector(
  [getUrlParams, (_, filterParameterNames: string[]) => filterParameterNames],
  (urlParams, filterParameterNames) => {
    const excludeList = new Set(['freeCancellation', 'chainIds'])

    return filterParameterNames.reduce((acc, current) => {
      const filterValue = getFilterValue(urlParams)(current)

      if (isNil(filterValue) || excludeList.has(current)) {
        return acc
      }

      return [...acc, current]
    }, [])
  }
)

/**
 * Get the `sortField` and `sortOrder` parameters from the URL
 */
export const getSortParams = createSelector(
  [getUrlParams, getSearchParameters],
  (urlParams, searchParameters): SortUrlParams => {
    const {userLocationSearch} = urlParams
    const isUserLocationSearch = userLocationSearch === '1'

    const sortField =
      omitSeoSortField(searchParameters.sortField) ||
      (isUserLocationSearch ? SORT_FIELD_DISTANCE : SORT_FIELD_POPULARITY)
    const sortOrder = searchParameters.sortOrder || SORT_ORDER_ASCENDING

    return {
      sortField,
      sortOrder
    } as SortUrlParams
  }
)

export const isSortAscendingByPrice = createSelector(
  [getSortParams],
  (sortParams): boolean => {
    const {sortField, sortOrder} = sortParams

    return sortField === SORT_FIELD_PRICE && sortOrder === SORT_ORDER_ASCENDING
  }
)

export const getIsSortByDistance = createSelector(
  [getSortParams],
  (sortParams): boolean => {
    const {sortField} = sortParams

    return sortField === SORT_FIELD_DISTANCE
  }
)

export const getListOfValues = (state: StateType) => state.search.listOfValues

const getLandingValue = (state: StateType): string =>
  state.search.landingQueryString

/**
 * Gets the original landing query string saved in the state
 */
export const getLandingQueryString = createSelector(
  [getLandingValue],
  landingQueryString => landingQueryString
)

/**
 * Gets the serialized version of the landing query string saved in the state
 */
export const getSerializedLandingQuery = createSelector(
  [getLandingQueryString],
  (landingQueryString): Record<string, string> =>
    parseQueryString(landingQueryString, true) as Record<string, string>
)

/**
 * Gets the landing value for profileId
 */
export const getLandingProfileId = createSelector(
  [getSerializedLandingQuery],
  (serializedLandingQuery): string => serializedLandingQuery.profile
)

/**
 * Gets the landing label param from the original landing query saved in the state
 */
export const getLandingLabel = createSelector(
  [getSerializedLandingQuery],
  (serializedLandingQuery): string => {
    const {label} = serializedLandingQuery
    return label || ''
  }
)

/**
 * Gets the landing checkIn param from the original landing query saved in the state
 */
export const getLandingCheckIn = createSelector(
  [getSerializedLandingQuery],
  (serializedLandingQuery): string => {
    const {checkIn} = serializedLandingQuery
    return checkIn || ''
  }
)

/**
 * Gets the landing checkInDistance param from the original landing query saved in the state
 */
export const getLandingCheckInDistance = createSelector(
  [getSerializedLandingQuery],
  (serializedLandingQuery): string => {
    const {checkInDistance} = serializedLandingQuery
    return checkInDistance || ''
  }
)

/**
 * The hotel the user landed on. On the standalone AP the hotel is a path
 * segment, so the landing query has no `hotelId` and the URL params carry it.
 * Links to another hotel's AP drop the meta landing token first, so a token
 * in an AP URL belongs to that page's hotel.
 */
export const getLandingHotelId = createSelector(
  [getSerializedLandingQuery, getUrlParams],
  (serializedLandingQuery, urlParams): string | undefined =>
    serializedLandingQuery.hotelId ?? urlParams.hotelId
)

/**
 * The hotel and dates the user landed with. The dates go through the same
 * validation as the search dates (`mlos`, check-out before check-in), so the
 * landing search itself always matches. A landing whose dates validation
 * cleared (in the past, too far ahead) matches nothing: the token was issued
 * for those dates.
 */
export const getLandingItinerary = createSelector(
  [getLandingHotelId, getLandingQueryString, getSerializedLandingQuery],
  (hotelId, landingQueryString, {checkIn: landingCheckIn}): Itinerary => {
    const dates = getCheckInCheckOutDates(
      new URLSearchParams(landingQueryString)
    )
    if (landingCheckIn && !dates.checkIn) return {}

    return {hotelId, ...dates}
  }
)

/**
 * Whether a request for `itinerary` may carry the meta landing token.
 * A landing with relative dates (`checkInDistance`) has no dates to compare,
 * so only the hotel is checked then.
 */
export const isLandingItinerary = (
  itinerary: Itinerary,
  landing: Itinerary
): boolean => {
  if (!landing.hotelId || itinerary.hotelId !== landing.hotelId) return false

  return (
    (!landing.checkIn || itinerary.checkIn === landing.checkIn) &&
    (!landing.checkOut || itinerary.checkOut === landing.checkOut)
  )
}

/**
 * Whether a request for this hotel and dates may carry the meta landing token.
 * A plain function: the result is a boolean, so there is nothing to memoize.
 */
export const getIsLandingItinerary = (
  state: StateType,
  hotelId?: string,
  checkIn?: string,
  checkOut?: string
): boolean =>
  isLandingItinerary({hotelId, checkIn, checkOut}, getLandingItinerary(state))

/**
 * Gets the traffic source from the original landing query saved in the state
 */
export const getLandingTrafficSource = createSelector(
  [getSerializedLandingQuery],
  (serializedLandingQuery): string => {
    const {label, utm_source: utmSource} = serializedLandingQuery
    return getTrafficSource(utmSource, label)
  }
)

/**
 * Get the `hotelId` parameter from the URL
 */
export const getHotelId = createSelector(
  [getSearchParameters],
  (searchParams): string | null | undefined => searchParams.hotelId
)

/**
 * Get the `placeId` parameter from the URL
 */
export const getPlaceId = createSelector(
  [getSearchParameters],
  (searchParams): string | null | undefined => searchParams.placeId
)

/**
 * Get the `hotelName` parameter from the URL
 */
export const getHotelName = createSelector(
  [getUrlParams],
  (urlParams): string | null | undefined => urlParams.hotelName
)

/**
 * Get the `noHostels` parameter from the URL
 */
export const getNoHostels = createSelector(
  [getUrlParams],
  (urlParams): BooleanUrlParam | null | undefined => urlParams.noHostels
)

/**
 * Get the `clickedOfferRate` parameter from the URL
 */
export const getClickedOfferRate = createSelector(
  [getSearchParameters],
  (searchParams): number => Number(searchParams.preferredRate)
)

/**
 * Get whether the `noHostels` filter is selected
 */
export const getIsHostelsFilterSelected = createSelector(
  [getUrlParams],
  (urlParams): boolean =>
    urlParams.propertyTypes?.includes(HOSTEL_PROPERTY_TYPE_ID)
)

/**
 * Get the `isUserSearch` value from the `search` slice of state
 */
export const getIsUserSearch = createSelector(
  [(state: StateType) => state.search.isUserSearch],
  isUserSearch => isUserSearch || false
)

/**
 * Get the `isDefaultDatesSearch` value from the `search` slice of state
 */
export const getIsDefaultDatesSearch = (state: StateType): boolean =>
  state.search.isDefaultDatesSearch || false

/**
 * Get the `isLandingExperience` value from the `search` slice of state
 */
export const getIsLandingExperience = (state: StateType): boolean =>
  state.search.isLandingExperience || false

/**
 * Get the `isNoDatesSearch` value from the `search` slice of state
 */
export const getIsNoDatesSearch = (state: StateType): boolean =>
  state.search.isNoDatesSearch || false

/**
 * Get the `isHomeSearch` value from the `search` slice of state
 */
export const getIsHomeSearch = (state: StateType): boolean =>
  state.search.isHomeSearch || false

/**
 * Get the `isDestinationPageSearch` value from the `search` slice of state
 */
export const getIsDestinationPageSearch = (state: StateType): boolean =>
  state.search.isDestinationPageSearch || false

/**
 * Get whether the search is a user geolocation search
 */
export const getIsUserLocationSearch = createSelector(
  (state: StateType): boolean => state.search.isUserLocationSearch,
  isUserLocationSearch => isUserLocationSearch || false
)

/**
 * Get the origin of the user location coordinates
 */
export const getUserLocationSearchType = (state: StateType) =>
  state.search.userLocationSearchType

export const getIsUserOrUrlLocationSearch = createSelector(
  getUserLocationSearchType,
  userLocationSearch =>
    [LocationSearchType.UserLocation, LocationSearchType.UrlLocation].includes(
      userLocationSearch
    )
)

/**
 * Get the `isFsmr` (force search multi-room) value from the `search` slice of state
 */
export const getIsFsmr = (state: StateType): boolean =>
  state.search.isFsmr || false

export const getIsOptimizeRooms = (state: StateType): boolean => {
  if (getHotelIdFromAccommodationPage() && isVioOfferOrBundleSelected())
    return false

  const params = getSearchParameters(state)

  return params?.optimizeRooms || false
}

/**
 * Get whether the search should be treated as a multi-room bundles search.
 * Based on whether fsmr or optimizeRooms is active.
 */
export const getIsMultiRoomBundlesSearch = createSelector(
  [getIsFsmr, getIsOptimizeRooms],
  (isFsmr, isOptimizeRooms): boolean => Boolean(isFsmr || isOptimizeRooms)
)

/**
 * Get the `isMapOpen` value from the `search` slice of state
 */
export const getIsMapOpen = (state: StateType): boolean =>
  state.search.isMapOpen || false

/**
 * Get the `activeHotelId` value from the `search` slice of state
 */
export const getActiveHotelId = (state: StateType): string =>
  state.search.activeHotelId

/**
 * Get whether the user landed with a check-in date or check-in distance
 */
const getHasLandingDates = createSelector(
  [getLandingCheckInDistance, getLandingCheckIn],
  (landingCheckInDistance, landingCheckIn): boolean => {
    return Boolean(landingCheckIn || landingCheckInDistance)
  }
)

/**
 * Get the `layout` value from the `search` slice of state
 * can return undefined as this is the initial value before
 * being set in the state from the URL params
 */
export const getLayout = createSelector(
  [(state: StateType) => state.search.layout],
  layout => layout
)

/**
 * Get the `searchDesktopHeaderHeight` value from the `search` slice of state
 */
export const getSearchDesktopHeaderHeight = (state: StateType): number =>
  state.search.searchDesktopHeaderHeight

/**
 * Get the `isTopHeaderHidden` value from the `search` slice of state
 */
export const getIsTopHeaderHidden = (state: StateType): boolean =>
  state.search.isTopHeaderHidden

const getHasDefaultLandingDates: (state: StateType) => boolean = createSelector(
  [getLandingLabel],
  label => hasDefaultDates({label})
)

export const isDatesDefault = (state: StateType): boolean =>
  !getHasLandingDates(state) || getHasDefaultLandingDates(state)

export const isLandingCheckInToday = createSelector(
  [getLandingCheckInDistance, getLandingCheckIn],
  (landingCheckInDistance, landingCheckIn): boolean => {
    if (landingCheckInDistance) {
      return Boolean(landingCheckInDistance === '0')
    }

    const today = dateToMiddayDate(new Date())
    return Boolean(differenceInDays(parseISO(landingCheckIn), today) === 0)
  }
)

export const getIsHotelDetailsOpenOnLanding = createSelector(
  [getSerializedLandingQuery],
  (serializedLandingQuery): boolean =>
    serializedLandingQuery.openHotelDetails === '1'
)

/**
 * Gets user landed directly on SRP or not
 */
export const getIsUserLandingDirectlyOnSrp = createSelector(
  [getSerializedLandingQuery],
  (landingQueryString): boolean => {
    if (isEmpty(landingQueryString)) return false

    return Boolean(
      landingQueryString['hotelId'] ?? landingQueryString['placeId']
    )
  }
)

/**
 * Determines whether to display the booking hint (tooltip or summary).
 *
 * @returns A boolean indicating if the search has conditions to show the date tooltip or booking summary
 */
export const getSearchHasBookingHint = createSelector(
  [
    getUrlParams,
    getIsLandingExperience,
    isDatesDefault,
    isLandingCheckInToday,
    getBrand,
    getIsUserLandingDirectlyOnSrp,
    getIsUserSearch
  ],
  (
    params,
    isLandingExperience,
    datesIsDefault,
    landingCheckInIsToday,
    brand,
    isUserLandingDirectlyOnSrp,
    isUserSearch
  ) => {
    // hasBookingHint is statically declared in brand config; safe to read directly.
    const bookingHint = brand.capabilities.search
      .hasBookingHint as ParameterizedCapabilityValue<BookingHintConfig>
    if (!bookingHint.enabled) return false

    const {trigger} = bookingHint.config

    if (trigger === 'urlParam' && params['bookingHint'] === '1') return true

    if (isLandingExperience && datesIsDefault && !landingCheckInIsToday)
      return true

    if (
      trigger === 'directSrpLanding' &&
      isUserLandingDirectlyOnSrp &&
      !isUserSearch
    ) {
      return true
    }

    return false
  }
)

export const getFreeCancellation = createSelector(
  [getUrlParams],
  (urlParams): boolean => Boolean(Number(urlParams.freeCancellation))
)

export const getCheckInDayDistance = createSelector(
  [getSearchParameters],
  ({checkIn}) => {
    const todayUtc = dateToMiddayDate(new Date())
    const checkInUtc = dateStringToMiddayDate(checkIn)
    return differenceInDays(checkInUtc, todayUtc)
  }
)

export const getIsCheckInAtLeastTwoDaysAway = createSelector(
  [getCheckInDayDistance],
  checkInDayDistance => checkInDayDistance >= 2
)

export const getIsCheckInAtLeastSixDaysAway = createSelector(
  [getCheckInDayDistance],
  checkInDayDistance => checkInDayDistance >= 6
)

/**
 * Get rate checker parameters (gsite + vf) from the URL
 */
export const getRateCheckerParams = createSelector(
  [getUrlParams],
  (urlParams): {gsite: string | undefined; vf: string | undefined} => ({
    gsite: getGsiteValueFromUrl(urlParams as SearchParamsType),
    vf: getVfValueFromUrl(urlParams as SearchParamsType)
  })
)

/**
 * Get the `clickId` parameter from the URL
 * This is a parameter passed from TripAdvisor
 */
export const getClickId = createSelector(
  [getUrlParams],
  (urlParams): string | undefined => urlParams.clickId
)

/**
 * Get if the current hotel is visited
 * @param hotelId - The hotel id
 * @returns A boolean of if the hotel has been visited
 */
export const getIsHotelVisited =
  (hotelId: string) =>
  (state: StateType): boolean =>
    state.search.visitedHotels[hotelId] ?? false

export const getSelectedAvailabilityDate = (state: StateType) =>
  state.search.selectedAvailabilityDate

/**
 * Get the url params that should be persisted
 */
export const getPersistedUrlParams = createSelector([getUrlParams], urlParams =>
  pick(persistedUrlParamKeys, urlParams)
)

/**
 * Get the base url params that should be persisted
 */
export const getPersistedBaseUrlParams = createSelector(
  [getUrlParams],
  urlParams => pick(persistedBaseUrlParamKeys, urlParams)
)

/**
 * Get the utm params that should be persisted
 */
export const getPersistedUTMParams = createSelector([getUrlParams], urlParams =>
  pick(persistedUTMParamsKeys, urlParams)
)

/*
 * Sticky OTA Mode flag, set after the first landing carrying
 * profile=23afbcc5e3 and persisted for the browser session
 * (cookie + sessionStorage; see hooks/useOtaMode/sessionStore).
 */
export const getIsStickyOtaMode = (state: StateType): boolean =>
  Boolean(state.search?.isStickyOtaMode)

export const getOfferModeOverride = (state: StateType): OfferMode | null =>
  state.search?.offerModeOverride ?? null

/**
 * Check if we are in OTA mode: the Offer mode override when set, otherwise the
 * brand-level capability (`capabilities.commerce.isOTAMode: true`), the profile
 * URL param or the sticky flag.
 */
export const getIsOTAMode = createSelector(
  [getBrand, getUrlParams, getIsStickyOtaMode, getOfferModeOverride],
  (brand, urlParams, sticky, offerModeOverride) =>
    offerModeOverride
      ? offerModeOverride === 'otaOnly'
      : (brand !== undefined &&
          resolveCapabilities(
            brand.capabilities,
            {} as CommerceCapabilityContext,
            {
              skipClientOnly: true
            }
          ).commerce.isOTAMode === true) ||
        isOtaModeProfile(urlParams.profile) ||
        sticky
)

/**
 * The raw `inAppFirst` URL param value (the in-app browser entry signal).
 */
export const getInAppFirstUrlParam = createSelector(
  [getUrlParams],
  urlParams => urlParams.inAppFirst
)

/*
 * Sticky InAppFirst flag, set after the first landing carrying `inAppFirst=1`
 * and persisted for the tab session (sessionStorage; see
 * hooks/useInAppFirst/sessionStore).
 */
export const getIsStickyInAppFirst = (state: StateType): boolean =>
  Boolean(state.search?.isStickyInAppFirst)

/**
 * Whether the InAppFirst session signal is present — the `inAppFirst=1` URL
 * param or the persisted sticky flag. This is the session half of the mode
 * only; the brand-eligibility half (`shell.supportsInAppFirst`) is applied in
 * the React layer via `useBrandCapabilities` (see `useInAppFirst`), so the
 * capability is consumed through the capability framework rather than reached
 * for directly off the brand here.
 *
 * The param is checked against both `'1'` and `1` because boolean URL params
 * are typed as `'0' | '1' | 0 | 1` (mirrors `isFlutterApp=1` / `vioEmbedded=1`).
 */
export const getHasInAppFirstSignal = createSelector(
  [getInAppFirstUrlParam, getIsStickyInAppFirst],
  (inAppFirstParam, sticky) =>
    inAppFirstParam === '1' || inAppFirstParam === 1 || sticky
)

/**
 * BoVio first UI on the SRP and the AP: OTA mode brands and the OTA First and OTA Only Offer mode overrides
 */
export const getIsBoVioEnvironment = createSelector(
  [(state: StateType) => state.search.isBoVioEnvironment],
  isBoVioEnvironment => isBoVioEnvironment
)

/**
 * The override goes first because the BoVio environment flag is never set on the home page
 */
export const getActiveOfferMode = createSelector(
  [getOfferModeOverride, getIsOTAMode, getIsBoVioEnvironment],
  (offerModeOverride, isOTAMode, isBoVioEnvironment): OfferMode =>
    offerModeOverride ??
    (isOTAMode ? 'otaOnly' : isBoVioEnvironment ? 'otaFirst' : 'meta')
)

export const getIsAppLockedDealExcluded = createSelector(
  [(state: StateType) => state.search.isAppLockedDealExcluded],
  isAppLockedDealExcluded => isAppLockedDealExcluded
)

/**
 * Use BoVio First profile for search/offers (originId) only when in BoVio environment and not OTA.
 * OTA brands keep BoVio First UI but use their normal profile for API calls.
 */
export const getUseBoVioFirstProfile = createSelector(
  [getIsBoVioEnvironment, getIsOTAMode],
  (isBoVioEnvironment, isOTAMode) => isBoVioEnvironment && !isOTAMode
)

/**
 * Returns the the esd value from the url
 */
export const getEsdUrlParam = createSelector(
  [getSearchParameters],
  (searchParams): string | undefined => searchParams?.metadata?.esd || undefined
)

/**
 * Returns the the epv value from the url
 */
export const getEpvUrlParam = createSelector(
  [getSearchParameters],
  (searchParams): string | undefined => searchParams?.metadata?.epv || undefined
)

/**
 * Returns the mlos (min length of stay) value from the url
 */
export const getMlos = createSelector(
  [getUrlParams],
  (urlParams): number | undefined => Number(urlParams.mlos) || undefined
)

/**
 * Returns whether the `Your choice` tag should be shown.
 * It should not be shown if the traffic is comes from TripAdvisor and the offer is private and the user is not authenticated.
 * This logic was introduced based on this discussion: https://viodotcom.slack.com/archives/CQ5HPT3L2/p1712821942795109
 * @param offer - Offer object
 * @returns - A boolean indicating if the tag should be shown
 */
export const getShouldShowYourChoiceTag =
  (offer?: GenericOffer | null, isSplitBooking = false) =>
  (state: RootState) => {
    const isTA = getIsFromTripAdvisor(state)
    const isPrivateDeal = getIsPrivateDeal(offer)(state)
    const isAuthenticated = getIsAuthenticated(state)

    if (isTA && isPrivateDeal && !isAuthenticated && !isSplitBooking) {
      return false
    }

    return offer?.tags?.includes(PREFERRED_RATE)
  }

/**
 * Returns the `freeTextSearchString` value from the `search` slice of state
 * @returns The `freeTextSearchString` value from the `search` slice of state
 */
export const getFreeTextSearchString = (state: StateType) =>
  state.search.freeTextSearchString

/**
 * Returns the the `profile` value from the url
 */
export const getProfileUrlParam = createSelector(
  [getUrlParams],
  (urlParams: SearchUrlParams): string => urlParams.profile ?? null
)

/**
 * Returns the map zoom level
 * Which is kept in sync with the map zoom level in MapBox to preserve even when the map
 * is hidden or unmounted
 */
export const getMapZoomLevel = (state: StateType) => state.search.mapZoomLevel

/**
 * Determines if the user came from a Search Results Page (SRP) to the current Accommodation Page (AP).
 *
 * This selector analyzes the landing referrer and search parameters to detect navigation source.
 *
 * This is used primarily for mobile AP back button behavior:
 * - If user came from SRP: close the window (since SRP is still open in background)
 * - If user came directly: navigate to SRP
 *
 * @param state - The Redux state
 * @returns boolean - true if user came from SRP, false otherwise
 */
export const getUserLandedFromSrp = createSelector(
  [(state: StateType) => state.search.landingReferrer, getBrand],
  (landingReferrer, brand) =>
    landingReferrer?.includes(brand.siteAddresses.address)
)

export const getCallToBook = createSelector(
  [(state: StateType) => state.search.callToBook],
  callToBook => callToBook
)

export const getIsCallToBookAvailable = createSelector(
  [getCallToBook],
  callToBook => {
    const {phoneNumber, status} = callToBook || {}
    return status === 'ready' && !!phoneNumber
  }
)

/**
 * Get the `dateValidationReason` value from the `search` slice of state
 */
export const getDateValidationReason = (
  state: StateType
): DateValidationReason | null => state.search.dateValidationReason

/**
 * Get the `isAnchorHotelHidden` value from the `search` slice of state
 * Added as part of sapi4eva-anchor-offer-level-filters experiment
 */
export const getIsAnchorHotelHidden = (state: StateType): boolean =>
  state.search.isAnchorHotelHidden || false

/**
 * Get the `isUserClosedHostelAlert` value from the `search` slice of state
 * Added as part of sapi4eva-anchor-offer-level-filters experiment
 */
export const getIsUserClosedHostelAlert = (state: StateType): boolean =>
  state.search.isUserClosedHostelAlert || false

export const getDealFreezeFilter = createSelector(
  [getUrlParams],
  (urlParams): boolean => Boolean(Number(urlParams.dealFreeze))
)

export const getIsUserInteractedWithFilters = (state: StateType): boolean =>
  state.search.isUserInteractedWithFilters || false
